import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import GridView from "../components/GridView";
import { Package, Link2, Copy, User } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// iter 178/178i: public read-only page for admin-generated product share
// links. On top of the read-only grid, a Sales Rep or Admin who happens
// to be logged in gets an extra bottom bar to pick a subset of products
// + a customer, then generate a customer-scoped quote link.
export default function ProductShareLinkPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [customerId, setCustomerId] = useState("");
  const [generating, setGenerating] = useState(false);
  const [customerLink, setCustomerLink] = useState(null);
  // iter 178p/178q: admin-only inline editing of description / specification /
  // per-entry USD+CAD prices + RMB cost + QTY_H_Q. Now per-product: only one
  // card is in edit mode at a time (editingId = product_id or null).
  const [editingId, setEditingId] = useState(null);
  const isAdmin = me && me.role === "admin";

  const refetch = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/public/products/${token}`);
      setData(r.data);
    } catch { /* keep prior snapshot */ }
  }, [token]);

  const handleEditProduct = useCallback(async (productId, payload) => {
    try {
      await axios.put(`${API}/inventory/products/${productId}`, payload, { withCredentials: true });
      toast.success("Saved");
      refetch();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    }
  }, [refetch]);

  const handleEditEntry = useCallback(async (entryId, payload) => {
    try {
      await axios.put(`${API}/inventory/${entryId}`, payload, { withCredentials: true });
      toast.success("Saved");
      refetch();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    }
  }, [refetch]);

  const handleRemoveProduct = useCallback(async (productId) => {
    // iter 178s: admin removes a product from this share link. Confirm
    // once — the button lives in the same header area as Edit so a
    // double-click by mistake would otherwise nuke a product.
    if (!window.confirm("Remove this product from the shared list?")) return;
    try {
      await axios.delete(`${API}/product-share-links/${token}/product/${productId}`, { withCredentials: true });
      toast.success("Removed");
      refetch();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to remove");
    }
  }, [refetch, token]);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/public/products/${token}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => {
        if (cancelled) return;
        const status = e?.response?.status;
        if (status === 404) setError("This share link doesn't exist.");
        else if (status === 410) setError("This share link has expired.");
        else setError("Something went wrong loading this share link.");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token]);

  // Best-effort auth probe. Public users get null; Sales Rep / Admin get
  // the picker.
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then((r) => { if (!cancelled) setMe(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const canPickCustomer = me && ["admin", "sales_rep"].includes(me.role);
  useEffect(() => {
    if (!canPickCustomer) return;
    axios.get(`${API}/customers?page=1&page_size=500`, { withCredentials: true })
      .then((r) => setCustomers(r.data?.items || r.data || []))
      .catch(() => {});
  }, [canPickCustomer]);

  const toggle = (pid) => setSelected((s) => {
    const next = new Set(s);
    if (next.has(pid)) next.delete(pid); else next.add(pid);
    return next;
  });

  const products = data?.products || [];
  const viewer = data?.viewer || null;
  const fmtRate = (v) => (v == null ? "-" : Number(v).toFixed(4).replace(/\.?0+$/, ""));
  const expiresLabel = data?.expires_at ? new Date(data.expires_at).toLocaleDateString() : "-";

  const handleGenerate = async () => {
    if (!customerId || selected.size === 0) return;
    setGenerating(true);
    try {
      const r = await axios.post(`${API}/product-share-links/${token}/customer-link`, {
        product_ids: Array.from(selected),
        customer_id: customerId,
      }, { withCredentials: true });
      const url = `${window.location.origin}/quote/customer/${r.data.token}`;
      setCustomerLink({ ...r.data, url });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to generate customer link");
    } finally {
      setGenerating(false);
    }
  };

  const copyLink = () => {
    if (!customerLink?.url) return;
    navigator.clipboard.writeText(customerLink.url).then(
      () => toast.success("Link copied"),
      () => toast.error("Copy failed"),
    );
  };

  if (loading) return <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-testid="share-link-loading"><p className="text-sm text-gray-400">Loading products…</p></div>;
  if (error) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-6" data-testid="share-link-error">
      <div className="max-w-md text-center">
        <Package size={40} strokeWidth={1.5} className="mx-auto text-gray-300 mb-3" />
        <h1 className="text-lg font-semibold text-gray-800 mb-1">Link Unavailable</h1>
        <p className="text-sm text-gray-500">{error}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 pb-24" data-testid="share-link-page">
      <header className="bg-white border-b border-gray-200 px-6 py-4 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-brand-600 text-white flex items-center justify-center">
              <Package size={16} strokeWidth={2} />
            </div>
            <div>
              <h1 className="text-sm font-semibold text-gray-900">Shared Products</h1>
              <p className="text-[11px] text-gray-500">{products.length} item(s) · link expires {expiresLabel}</p>
            </div>
          </div>
          {/* iter 258: logged-in viewer context — who I am + the rates that
              drive the CAD formula. Public visitors see nothing here. */}
          {viewer && (
            <div className="flex items-center gap-4 text-[11px] text-gray-600 flex-wrap justify-end" data-testid="share-viewer-info">
              <span className="inline-flex items-center gap-1" data-testid="share-viewer-user"><User size={12} strokeWidth={1.75} className="text-gray-400" /><span className="font-medium text-gray-800">{viewer.name || viewer.email}</span></span>
              <span data-testid="share-viewer-usd-rmb"><span className="text-gray-400">USD→RMB</span> <span className="font-mono font-medium text-gray-800">{fmtRate(viewer.usd_to_rmb_rate)}</span></span>
              <span data-testid="share-viewer-usd-cad"><span className="text-gray-400">USD→CAD</span> <span className="font-mono font-medium text-gray-800">{fmtRate(viewer.usd_to_cad_rate)}</span></span>
              <span data-testid="share-viewer-sea-freight"><span className="text-gray-400">Sea Freight / 40HQ</span> <span className="font-mono font-medium text-gray-800">{viewer.sea_freight_per_container == null ? "-" : `$${Number(viewer.sea_freight_per_container).toLocaleString()}`}</span></span>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-6">
        {products.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-xl py-24 text-center">
            <p className="text-sm text-gray-400">No products in this share link.</p>
          </div>
        ) : (
          <GridView
            products={products}
            density="detailed"
            priceCurrency="USD"
            readOnly
            canEdit={isAdmin}
            editingId={editingId}
            onToggleEdit={(pid) => setEditingId((cur) => cur === pid ? null : pid)}
            onEditProduct={handleEditProduct}
            onEditEntry={handleEditEntry}
            onRemove={isAdmin ? handleRemoveProduct : undefined}
            rates={viewer}
            loading={false}
            selected={selected}
            onToggleSelected={(pid) => toggle(pid)}
            onCardClick={() => {}}
            onCartClick={() => {}}
            onAddVariant={() => {}}
          />
        )}
      </main>

      {/* iter 178k: Sign-in nudge for public visitors who select products.
          Redirects to /login and comes back to this share URL after auth. */}
      {selected.size > 0 && !me && (
        <div className="fixed bottom-0 left-0 right-0 bg-amber-500 text-white px-6 py-3 shadow-lg" data-testid="signin-nudge-bar">
          <div className="max-w-7xl mx-auto flex items-center justify-between gap-3">
            <span className="text-sm"><span className="font-semibold">{selected.size}</span> selected · Sign in as Sales Rep to pick a customer and generate a quote link</span>
            <a
              href={`/login?next=${encodeURIComponent(window.location.pathname)}`}
              data-testid="signin-nudge-button"
              className="h-8 px-4 rounded-lg bg-white text-amber-700 hover:bg-gray-100 text-xs font-medium inline-flex items-center"
            >
              Sign in
            </a>
          </div>
        </div>
      )}

      {/* iter 178i: Bottom action bar for Sales Rep / Admin. Public users
          just see the products with no bar. */}
      {selected.size > 0 && canPickCustomer && !customerLink && (
        <div className="fixed bottom-0 left-0 right-0 bg-gray-900 text-white px-6 py-3 shadow-lg" data-testid="customer-link-bar">
          <div className="max-w-7xl mx-auto flex items-center gap-3">
            <span className="text-sm"><span className="font-semibold">{selected.size}</span> selected</span>
            <div className="flex-1 flex items-center gap-2 max-w-md">
              <User size={14} strokeWidth={1.75} className="text-gray-400" />
              <select
                data-testid="customer-link-customer-select"
                value={customerId}
                onChange={(e) => setCustomerId(e.target.value)}
                className="flex-1 h-8 px-2 rounded-md bg-gray-800 border border-gray-700 text-sm text-white"
              >
                <option value="">Select customer…</option>
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>{c.name || c.company_name} {c.currency ? `(${c.currency})` : ""}</option>
                ))}
              </select>
            </div>
            <button
              data-testid="customer-link-generate"
              onClick={handleGenerate}
              disabled={!customerId || generating}
              className="h-8 px-3 rounded-lg bg-white text-gray-900 hover:bg-gray-100 text-xs font-medium disabled:opacity-40"
            >
              <Link2 size={12} strokeWidth={2} className="inline mr-1.5" />
              {generating ? "Generating…" : "Generate Customer Link"}
            </button>
          </div>
        </div>
      )}

      {/* Result banner after generation */}
      {customerLink && (
        <div className="fixed bottom-0 left-0 right-0 bg-emerald-600 text-white px-6 py-3 shadow-lg" data-testid="customer-link-result">
          <div className="max-w-7xl mx-auto flex items-center gap-3">
            <span className="text-sm font-medium">✓ Link for <span className="font-semibold">{customerLink.customer_name}</span> ready:</span>
            <input readOnly value={customerLink.url} onFocus={(e) => e.target.select()} className="flex-1 h-8 px-2 rounded-md bg-emerald-700 border border-emerald-500 text-xs font-mono" data-testid="customer-link-url" />
            <button onClick={copyLink} className="h-8 px-3 rounded-lg bg-white text-emerald-700 hover:bg-gray-100 text-xs font-medium" data-testid="customer-link-copy">
              <Copy size={12} strokeWidth={2} className="inline mr-1.5" /> Copy
            </button>
            <button onClick={() => { setCustomerLink(null); setSelected(new Set()); setCustomerId(""); }} className="h-8 px-3 rounded-lg bg-transparent border border-emerald-400 text-white hover:bg-emerald-700 text-xs">
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
